import type { AudioClip, Project } from '@/domain/types';
import { MAX_CLIP_GAIN, MIN_CLIP_GAIN, clipSourceAtTick, clipTickAtSource } from '@/domain/clipGain';
import { clipEndTicks, clipLengthTicks, signatureMapOf, tempoMapOf } from '@/domain/selectors';
import {
  signatureSegments,
  ticksPerBar,
  ticksPerBeat,
  type NormalisedSignatureMap,
} from '@/timing/tempoMap';
import { MAX_TRACK_HEIGHT_SCALE, MIN_TRACK_HEIGHT_SCALE } from '@/domain/project';

/**
 * Timeline geometry.
 *
 * One conversion pair, used by drawing, hit testing and every drag gesture, so
 * that what is drawn and what is clickable cannot disagree. Zoom is expressed
 * as pixels per quarter note rather than pixels per second, because musical
 * spacing is what the grid, the snap and the eye all work in.
 */

export interface TimelineView {
  pixelsPerBeat: number;
  scrollTicks: number;
  scrollTop: number;
  ppq: number;
  width: number;
  height: number;
}

export const MIN_ZOOM = 3;
export const MAX_ZOOM = 480;

export function tickToX(view: TimelineView, tick: number): number {
  return ((tick - view.scrollTicks) / view.ppq) * view.pixelsPerBeat;
}

export function xToTick(view: TimelineView, x: number): number {
  return view.scrollTicks + (x / view.pixelsPerBeat) * view.ppq;
}

export function ticksPerPixel(view: TimelineView): number {
  return view.ppq / view.pixelsPerBeat;
}

export interface LaneLayout {
  trackId: string;
  /** The automation lane shown here, or null for a clip lane. */
  laneId: string | null;
  /** The take lane shown here (1 and up), or null for the comp lane. */
  takeLane: number | null;
  top: number;
  height: number;
}

/** Take lanes are shorter than the comp: they are alternatives, not the take. */
export const TAKE_LANE_RATIO = 0.62;
export const MIN_TAKE_LANE_HEIGHT = 34;

/**
 * Vertical layout, in content coordinates (before `scrollTop`).
 *
 * Automation lanes are laid out as ordinary lanes directly under their track,
 * so one layout pass serves drawing, hit testing and the header column — and
 * the headers cannot drift out of alignment with the lanes they label.
 */
/** The vertical zoom multiplier, defended against a project that predates it. */
export function trackHeightScale(project: Project): number {
  const raw = project.view.trackHeightScale;
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  return Math.min(MAX_TRACK_HEIGHT_SCALE, Math.max(MIN_TRACK_HEIGHT_SCALE, raw));
}

export function laneLayout(project: Project): { lanes: LaneLayout[]; totalHeight: number } {
  const lanes: LaneLayout[] = [];
  const scale = trackHeightScale(project);
  // Whole pixels only: lanes are drawn on a canvas, and fractional tops would
  // leave seams between the header column and the timeline.
  const scaled = (value: number): number => Math.max(1, Math.round(value * scale));
  let top = 0;
  for (const trackId of project.trackOrder) {
    const track = project.tracks[trackId];
    if (!track) continue;

    // A collapsed track stays collapsed; vertical zoom is not an unfolding.
    const height = track.collapsed ? 26 : scaled(track.height);
    lanes.push({ trackId, laneId: null, takeLane: null, top, height });
    top += height;

    if (track.collapsed) continue;

    if (track.showTakeLanes) {
      const takeHeight = Math.max(scaled(MIN_TAKE_LANE_HEIGHT), Math.round(height * TAKE_LANE_RATIO));
      for (let takeLane = 1; takeLane <= takeLaneCount(project, trackId); takeLane++) {
        lanes.push({ trackId, laneId: null, takeLane, top, height: takeHeight });
        top += takeHeight;
      }
    }

    if (!track.showAutomation) continue;
    for (const laneId of track.automationLaneIds) {
      const lane = project.automation[laneId];
      if (!lane) continue;
      const laneHeight = scaled(lane.height);
      lanes.push({ trackId, laneId, takeLane: null, top, height: laneHeight });
      top += laneHeight;
    }
  }
  return { lanes, totalHeight: top };
}

/** Only the lanes that hold clips: the comp lane and any take lanes. */
export function clipLanes(lanes: readonly LaneLayout[]): LaneLayout[] {
  return lanes.filter((lane) => lane.laneId === null);
}

/** How many take lanes a track has material on. */
export function takeLaneCount(project: Project, trackId: string): number {
  const track = project.tracks[trackId];
  if (!track) return 0;
  let highest = 0;
  for (const clipId of track.clipIds) {
    const lane = project.clips[clipId]?.takeLane ?? 0;
    if (lane > highest) highest = lane;
  }
  return highest;
}

export function laneAtY(lanes: readonly LaneLayout[], y: number): LaneLayout | null {
  for (const lane of lanes) {
    if (y >= lane.top && y < lane.top + lane.height) return lane;
  }
  return null;
}

export interface ClipRect {
  clipId: string;
  trackId: string;
  /** 0 for the comp lane, 1 and up for take lanes. */
  takeLane: number;
  x: number;
  width: number;
  y: number;
  height: number;
}

/** Screen rectangles for clips intersecting the viewport. */
export function visibleClipRects(
  project: Project,
  view: TimelineView,
  lanes: readonly LaneLayout[],
): ClipRect[] {
  const out: ClipRect[] = [];
  const firstTick = view.scrollTicks;
  const lastTick = xToTick(view, view.width);

  for (const lane of lanes) {
    if (lane.laneId !== null) continue;
    const y = lane.top - view.scrollTop;
    if (y + lane.height < 0 || y > view.height) continue;
    const track = project.tracks[lane.trackId];
    if (!track) continue;

    const wantedLane = lane.takeLane ?? 0;
    for (const clipId of track.clipIds) {
      const clip = project.clips[clipId];
      if (!clip || clip.takeLane !== wantedLane) continue;
      const length = clipLengthTicks(project, clip);
      if (clip.startTicks + length < firstTick || clip.startTicks > lastTick) continue;
      const x = tickToX(view, clip.startTicks);
      const width = Math.max(2, (length / view.ppq) * view.pixelsPerBeat);
      out.push({ clipId, trackId: lane.trackId, takeLane: wantedLane, x, width, y, height: lane.height });
    }
  }
  return out;
}

export interface GridLine {
  tick: number;
  x: number;
  kind: 'bar' | 'beat' | 'sub';
  label: string | null;
}

/**
 * Grid lines for the visible range, thinned so lines never come closer than a
 * readable spacing. Beats disappear before bars; subdivisions appear only when
 * there is room for them.
 */
export function gridLines(project: Project, view: TimelineView): GridLine[] {
  const sigMap: NormalisedSignatureMap = signatureMapOf(project);
  const startTick = Math.max(0, view.scrollTicks);
  const endTick = xToTick(view, view.width);

  const lines: GridLine[] = [];
  // Segment by segment: bar and beat lengths change at every meter event, so a
  // single stride across the whole viewport would drift after the first one.
  for (const segment of signatureSegments(sigMap, startTick, endTick)) {
    const barTicks = ticksPerBar(segment.sig, sigMap.ppq);
    const beatTicks = ticksPerBeat(segment.sig, sigMap.ppq);

    const pxPerBar = (barTicks / view.ppq) * view.pixelsPerBeat;
    const pxPerBeat = (beatTicks / view.ppq) * view.pixelsPerBeat;

    // Step chosen so bar numbers never overlap.
    const barStep = pxPerBar >= 56 ? 1 : pxPerBar >= 28 ? 2 : pxPerBar >= 14 ? 4 : pxPerBar >= 7 ? 8 : 16;
    const showBeats = pxPerBeat >= 12;
    const subDivisions = pxPerBeat >= 96 ? 4 : pxPerBeat >= 48 ? 2 : 1;

    const from = Math.max(startTick, segment.startTick);
    const to = Math.min(endTick, segment.endTick);
    const firstBarIndex = Math.max(0, Math.floor((from - segment.startTick) / barTicks));
    const lastBarIndex = Math.ceil((to - segment.startTick) / barTicks);

    for (let barIndex = firstBarIndex; barIndex <= lastBarIndex; barIndex++) {
      const barTick = segment.startTick + barIndex * barTicks;
      if (barTick >= segment.endTick) break;
      if (barIndex % barStep === 0) {
        lines.push({
          tick: barTick,
          x: tickToX(view, barTick),
          kind: 'bar',
          label: `${segment.startBar + barIndex + 1}`,
        });
      }
      if (!showBeats) continue;
      for (let beat = 1; beat < segment.sig.numerator; beat++) {
        const beatTick = barTick + beat * beatTicks;
        if (beatTick >= segment.endTick) break;
        lines.push({ tick: beatTick, x: tickToX(view, beatTick), kind: 'beat', label: null });
      }
      if (subDivisions > 1) {
        for (let beat = 0; beat < segment.sig.numerator; beat++) {
          for (let s = 1; s < subDivisions; s++) {
            const tick = barTick + beat * beatTicks + (s * beatTicks) / subDivisions;
            if (tick >= segment.endTick) break;
            lines.push({ tick, x: tickToX(view, tick), kind: 'sub', label: null });
          }
        }
      }
    }
  }
  return lines;
}

/** Where the tempo changes in a tick range, for the ruler to label. */
export interface TempoMarker {
  tick: number;
  x: number;
  label: string;
  ramps: boolean;
}

export function tempoMarkers(project: Project, view: TimelineView): TempoMarker[] {
  const map = tempoMapOf(project);
  const startTick = Math.max(0, view.scrollTicks);
  const endTick = xToTick(view, view.width);
  const out: TempoMarker[] = [];
  for (let i = 0; i < map.events.length; i++) {
    const event = map.events[i];
    // The opening tempo is shown in the transport, not on the ruler; only
    // changes are worth the ink.
    if (event.tick <= 0 || event.tick < startTick || event.tick > endTick) continue;
    const ramps = event.ramp === true && i + 1 < map.events.length;
    out.push({
      tick: event.tick,
      x: tickToX(view, event.tick),
      label: `${Math.round(event.bpm * 100) / 100}`,
      ramps,
    });
  }
  return out;
}

/** Where the meter changes in a tick range, for the ruler to label. */
export interface MeterMarker {
  tick: number;
  x: number;
  label: string;
}

export function meterMarkers(project: Project, view: TimelineView): MeterMarker[] {
  const sigMap = signatureMapOf(project);
  const startTick = Math.max(0, view.scrollTicks);
  const endTick = xToTick(view, view.width);
  return signatureSegments(sigMap, startTick, endTick)
    .filter((segment) => segment.startTick >= startTick)
    .map((segment) => ({
      tick: segment.startTick,
      x: tickToX(view, segment.startTick),
      label: `${segment.sig.numerator}/${segment.sig.denominator}`,
    }));
}

/** Zoom about a fixed screen position, so the point under the cursor stays put. */
export function zoomAround(view: TimelineView, factor: number, anchorX: number): { pixelsPerBeat: number; scrollTicks: number } {
  const anchorTick = xToTick(view, anchorX);
  const pixelsPerBeat = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, view.pixelsPerBeat * factor));
  const scrollTicks = Math.max(0, anchorTick - (anchorX / pixelsPerBeat) * view.ppq);
  return { pixelsPerBeat, scrollTicks };
}

/** Lane-local y position for a normalised automation value. */
export function automationValueToY(lane: LaneLayout, value: number, scrollTop: number): number {
  const top = lane.top - scrollTop + AUTOMATION_PADDING;
  const usable = Math.max(1, lane.height - AUTOMATION_PADDING * 2);
  return top + (1 - Math.min(1, Math.max(0, value))) * usable;
}

/** Normalised automation value for a screen y position within a lane. */
export function yToAutomationValue(lane: LaneLayout, y: number, scrollTop: number): number {
  const top = lane.top - scrollTop + AUTOMATION_PADDING;
  const usable = Math.max(1, lane.height - AUTOMATION_PADDING * 2);
  return Math.min(1, Math.max(0, 1 - (y - top) / usable));
}

/**
 * Points are drawn as discs, so the usable value range is inset by their radius
 * plus a little. Without it, a point at 0 or 1 would be half outside its lane
 * and impossible to grab.
 */
export const AUTOMATION_PADDING = 7;

/**
 * Where a clip's gain envelope lives inside its rectangle.
 *
 * The clip's header strip is left alone — it carries the name, and dragging a
 * level on top of a label is how you end up clicking the wrong thing — so the
 * envelope occupies the body underneath it. `drawClips` computes the same header
 * height, and both take it from here so a point is drawn where it can be
 * grabbed.
 */
export interface ClipGainArea {
  top: number;
  height: number;
}

export function clipGainArea(rect: { y: number; height: number }): ClipGainArea {
  const y = rect.y + 2;
  const height = rect.height - 5;
  const headerH = Math.min(15, Math.max(11, height * 0.22));
  return { top: y + headerH, height: Math.max(1, height - headerH) };
}

/**
 * A gain, as a y inside the clip body, and back.
 *
 * The full range fits the body with unity halfway up, which is the property that
 * makes a ride readable at a glance: the line sits in the middle when nothing is
 * happening, and any departure from the middle is a change.
 */
export function clipGainToY(area: ClipGainArea, gain: number): number {
  const clamped = Math.min(MAX_CLIP_GAIN, Math.max(MIN_CLIP_GAIN, gain));
  return area.top + area.height * (1 - clamped / MAX_CLIP_GAIN);
}

export function yToClipGain(area: ClipGainArea, y: number): number {
  const fraction = 1 - (y - area.top) / area.height;
  return Math.min(MAX_CLIP_GAIN, Math.max(MIN_CLIP_GAIN, fraction * MAX_CLIP_GAIN));
}

/**
 * A position in the clip's source audio, as an x inside the clip rectangle.
 *
 * The mapping is proportional: a clip covers `durationSec` of source across its
 * whole width. That is exact for every clip whose rate is constant — which is
 * all of them except one that carries warp markers, where the audio is
 * deliberately bent against the grid and the drawn position drifts from the
 * heard one by however far the marker pulls. The scheduler does that mapping
 * properly; the drawing does not, and says so rather than pretending.
 */
/**
 * Where a moment of a clip's source is drawn, and which moment is under an x.
 *
 * Through the scheduler's own mapping rather than an even spread of the source
 * across the clip's width, so a ride point on a warped clip — or on a clip
 * lying under a tempo change, or a reversed one — is drawn on the pixel it is
 * heard at. The waveform goes through the same pair, which is what keeps the
 * point on the transient it was put on.
 */
export function clipSourceToX(project: Project, view: TimelineView, clip: AudioClip, sourceSec: number): number {
  return tickToX(view, clipTickAtSource(project, clip, sourceSec));
}

export function xToClipSource(project: Project, view: TimelineView, clip: AudioClip, x: number): number {
  const tick = Math.max(clip.startTicks, Math.min(clipEndTicks(project, clip), xToTick(view, x)));
  const source = clipSourceAtTick(project, clip, tick);
  return Math.max(clip.offsetSec, Math.min(clip.offsetSec + clip.durationSec, source));
}

/** How close a pointer has to be to a gain point to grab it, in pixels. */
export const CLIP_GAIN_GRAB_PX = 7;

/** The envelope point under a pointer, if there is one within grabbing distance. */
export function clipGainPointAt(
  project: Project,
  view: TimelineView,
  clip: AudioClip,
  rect: { y: number; height: number },
  x: number,
  y: number,
): string | null {
  const area = clipGainArea(rect);
  let best: { id: string; distance: number } | null = null;
  for (const point of clip.gainEnvelope) {
    const px = clipSourceToX(project, view, clip, point.sourceSec);
    const py = clipGainToY(area, point.gain);
    const distance = Math.hypot(px - x, py - y);
    if (distance <= CLIP_GAIN_GRAB_PX && (!best || distance < best.distance)) best = { id: point.id, distance };
  }
  return best?.id ?? null;
}
